import { useEffect, useState } from 'react';
import { PageLocation, TransactionalCurrency } from '../types/enums';
import {
  Ecommerce,
  ProductListItemViewModel,
  ProductViewModel,
  Website
} from '../types/generated';
import formatCurrency from '../utilities/formatCurrency';
import Roundels from './Roundels';
import { ResponsiveProductImage } from './ResponsiveProductImage';
import { addToBasket } from '../apps/basket/basketApi';
import popupAlert from '../utilities/popup-alert';
import updateQuantityBadge from '../utilities/updateQuantityBadge';
import StockIndicator from '../components/StockIndicator';
import Offer from '../apps/Offer/Offer';
import { getProductByProductId } from '../apps/product/productPageApi';
import ProductsILove from '../apps/product/components/ProductsILove';
import {
  addGA4ProductEvent,
  getGA4ListName,
  getGA4ListId
} from '../helpers/ga4';
import { displayProductWasPrice } from '../utilities/displayProductWasPrice';
import { CustomerDiscountText } from './CustomerDiscountText';

export interface ProductPodProps extends ProductListItemViewModel {
  transactionalCurrency: TransactionalCurrency;
  showSecondaryPrice: boolean;
  website: Website;
  location: PageLocation;
  onAddToBasketClick?: (
    productId: number,
    quantity: number,
    catalogueRef: string
  ) => void;
  onTrackAddToBasketMonetate?: () => void;
  isCssFavouritesEnabled?: boolean;
}

export default function ProductPod(props: ProductPodProps) {
  const [isJustAdded, setIsJustAdded] = useState(false);
  const [offerFreeProduct, setOfferFreeProduct] =
    useState<ProductViewModel | null>(null);

  const displayWasPrice = displayProductWasPrice(
    props.isContractPrice,
    props.showSecondaryPrice,
    props.pricingInfo
  );

  function handleAddToBasket() {
    const qty = props.isUnitPriceDisabled ? props.priceBreakMinUnits : 1;
    if (props.onAddToBasketClick) {
      props.onAddToBasketClick(props.productId, qty, props.catalogueRef || '');
    } else {
      // const qty = typeof quantity === 'string' ? 1 : quantity;
      getProductByProductId(props.productId).then((item: ProductViewModel) => {
        addGA4ProductEvent(
          'add_to_cart',
          item,
          {
            item_list_id: getGA4ListId(props.location),
            item_list_name: getGA4ListName(props.location)
          } as Ecommerce,
          qty
        );
      });

      addToBasket(props.productId, qty, props.catalogueRef, true)
        .then((response) => {
          if (response.success && response.warning) {
            popupAlert.showWarning(response.message);
          } else if (!response.success) {
            popupAlert.showNegative(response.message);
          } else {
            popupAlert.showPositive(response.message);
            if (props.onTrackAddToBasketMonetate)
              props.onTrackAddToBasketMonetate();
          }
          updateQuantityBadge(response.basketItemCount);
          setIsJustAdded(true);
        })
        .catch((error: Error) => {
          console.error(error);
        });
    }
  }

  function handleProductView() {
    getProductByProductId(props.productId).then((item: ProductViewModel) => {
      addGA4ProductEvent('select_item', item, {});
    });
  }

  function renderSavings() {
    let bestPriceBreak = props.priceBreaks
      ? props.priceBreaks[props.priceBreaks.length - 1]
      : null;
    if (
      bestPriceBreak &&
      bestPriceBreak.pricingInfo.savingsPercentage > 0 &&
      !displayWasPrice
    ) {
      return (
        <span className="savings savings--plp">
          Save up to {bestPriceBreak.pricingInfo.savingsPercentage}%
        </span>
      );
    } else if (props.pricingInfo.primarySavings > 0) {
      return (
        <span className="savings savings--plp">
          {props.isVariant || props.isPriceBreakPrice
            ? 'Savings from '
            : 'Save '}
          {formatCurrency(
            props.pricingInfo.primarySavings,
            props.transactionalCurrency
          )}
        </span>
      );
    }
  }

  function getPrimaryPriceWording() {
    if (displayWasPrice) {
      if (props.isVariant || props.isPriceBreakPrice) {
        return 'NOW from ';
      }
      return 'NOW ';
    } else if (props.isVariant || props.isPriceBreakPrice) {
      return 'From ';
    }
  }

  useEffect(() => {
    if (props.offerFreeProductID && props.offerFreeProductID > 0) {
      getProductByProductId(props.offerFreeProductID).then(
        (data: ProductViewModel) => {
          setOfferFreeProduct(data);
        }
      );
    }
  }, [props.offerFreeProductID]);

  return (
    <article className="product-pod">
      <div className="product-pod__body">
        <a
          href={props.urlPath || ''}
          className="product-pod__image-link"
          onClick={handleProductView}
        >
          <div className="roundel-wrapper">
            <Roundels roundel={props.roundel} />
            <Roundels roundel={props.secondaryRoundel} />
          </div>
          <ResponsiveProductImage
            alt={props.name || ''}
            src={
              props.imageUrl ||
              'https://cdn.images.fecom-media.com/image_coming_soon.jpg'
            }
            xs={360}
            sm={228}
            //md={228}
            lg={198}
            //xl={172}
            xxl={228}
            className="product-pod__image lazyload"
          />
          <ProductsILove
            catalogueRef={props.catalogueRef}
            isEnabled={
              props.isCssFavouritesEnabled
                ? props.isVariant
                  ? false
                  : true
                : false
            }
          />
          {/* @Html.ResponsiveImage(Model.ImageUrl, "", new ResponsiveImageSizes(sm:
          228, md: 228, lg: 198, xl: 172 ), "product-pod__image") */}
          {props.isVariant && !props.isRestricted && (
            <img
              alt="More Colour Options"
              className="product-pod__more-options"
              src="/images/colour-options.svg"
            />
          )}
        </a>
        <a
          href={props.urlPath || ''}
          className="product-pod__link"
          onClick={handleProductView}
        >
          <div className="product-pod__title">
            {props.name}{' '}
            {/* @if (!Model.UnitOfSale.IsNullOrEmpty() && Model.UnitOfSale != "Each") */}
            {props.unitOfSale && props.unitOfSale !== 'Each' && (
              <span className="product-pod__uos">{props.unitOfSale}</span>
            )}
          </div>
        </a>
      </div>
      <footer className="product-pod__footer">
        {!props.isVariant && (
          <div className="product-pod__code">{props.catalogueRef}</div>
        )}
        {props.stockInfo && (
          <StockIndicator
            stockInfo={props.stockInfo}
            supplierID={props.supplierID}
            isThirdPartyDDelivery={props.isThirdPartyDDelivery}
            isRestricted={props.isRestricted}
          />
        )}

        <CustomerDiscountText
          isVariant={props.isVariant}
          isContractPrice={props.isContractPrice}
          isHeaderDiscounted={props.isHeaderDiscounted}
          styleModifier={'--plp'}
        />

        {displayWasPrice && (
          <div className="product-pod__was-price">
            Was{' '}
            {`${props.isVariant ? 'from' : ''} ${formatCurrency(
              props.pricingInfo.primaryWasPrice.value,
              props.transactionalCurrency
            )}`}
          </div>
        )}

        <div
          className={`product-pod__price ${
            displayWasPrice ? 'product-pod__price--now' : ''
          }`}
        >
          {getPrimaryPriceWording()}
          {formatCurrency(
            props.pricingInfo.primaryPrice.value,
            props.transactionalCurrency
          )}

          {props.showSecondaryPrice == false && (
            <span className="price-primary-text">
              {props.pricingInfo.primaryPriceText}
            </span>
          )}

          {!props.isVariant &&
            props.isUnitPriceDisabled &&
            props.priceBreakMinUnits > 0 && (
              <span className="product-pod__price product-pod__price--moq">
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Minimum Quantity{' '}
                {props.priceBreakMinUnits}+
              </span>
            )}
        </div>

        {props.showSecondaryPrice && (
          <div className={`product-pod__price product-pod__price--secondary`}>
            {formatCurrency(
              props.pricingInfo.secondaryPrice.value,
              props.transactionalCurrency
            )}
            <span className="price-secondary-text">
              {props.pricingInfo.secondaryPriceText}
            </span>
          </div>
        )}
        {renderSavings()}
        <Offer
          offer={{
            productCatalogueRef: props.catalogueRef,
            offerDescription: props.offerDescription,
            offerFreeProductId: props.offerFreeProductID,
            offerName: props.offerName,
            offerQuantityBuy: props.offerQuantityBuy,
            offerQuantityFree: props.offerQuantityFree,
            offerTypeId: props.offerTypeID,
            offerTypeName: props.offerTypeName
          }}
          className="offer-details-box"
          offerFreeProduct={offerFreeProduct}
        />

        {/* @if (!Model.IsRestricted) */}
        {!props.isRestricted &&
          !props.isVariant &&
          !props.isTemporarilyUnavailable && (
            <div className="product-pod__add-to-basket">
              {(props.isAdded || isJustAdded) && (
                <button
                  type="submit"
                  className="btn btn-primary btn- btn-block"
                  onClick={handleAddToBasket}
                >
                  Added to Basket
                </button>
              )}
              {!props.isAdded && !isJustAdded && (
                <button
                  type="submit"
                  className="btn btn-primary btn- btn-block"
                  onClick={handleAddToBasket}
                >
                  Add to Basket
                </button>
              )}
            </div>
          )}

        {props.isVariant && !props.isRestricted && (
          <div className="product-pod__add-to-basket">
            <a
              className="btn btn-primary btn- btn-block"
              href={props.urlPath || ''}
              onClick={handleProductView}
            >
              More Options
            </a>
          </div>
        )}

        {(props.isRestricted ||
          (props.isTemporarilyUnavailable && !props.isVariant)) && (
          <div className="product-pod__add-to-basket">
            <button
              type="submit"
              disabled
              className="btn btn-primary btn- btn-block"
            >
              {props.isRestricted
                ? 'Product is restricted'
                : 'Temporarily Unavailable'}
            </button>
          </div>
        )}
      </footer>
    </article>
  );
}
